import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";

import { AuthContext } from "../context/AuthContext";

import { motion } from "framer-motion";

import {
  Building2,
  ShieldCheck,
  Users,
} from "lucide-react";

export default function SelectOrganization() {

  const navigate = useNavigate();

  const {
    setRole,
    setOrgId,
    setOrgSlug,
  } = useContext(AuthContext);

  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  // =====================================================
  // 🔥 LOAD ORGANIZATIONS
  // =====================================================

  useEffect(() => {

    try {

      const stored =
        JSON.parse(localStorage.getItem("orgList")) || [];

      if (!Array.isArray(stored)) {
        setOrgs([]);
      } else {
        setOrgs(stored);
      }

    } catch (error) {

      console.error("ORG LOAD ERROR:", error);

      setOrgs([]);

    } finally {

      setLoading(false);

    }

  }, []);

  // =====================================================
  // 🔥 SELECT ORG
  // =====================================================

  const selectOrg = (org) => {

    if (!org?.orgId || !org?.orgSlug || !org?.role) {
      alert("Invalid organization data");
      return;
    }

    // 🔥 STORE
    localStorage.setItem("orgId", org.orgId);
    localStorage.setItem("role", org.role);
    localStorage.setItem("orgSlug", org.orgSlug);

    // 🔥 REMOVE TEMP LIST
    localStorage.removeItem("orgList");

    // 🔥 CONTEXT
    setOrgId(org.orgId);
    setRole(org.role);
    setOrgSlug(org.orgSlug);

    // =====================================================
    // 🔥 REDIRECT
    // =====================================================

    if (org.role === "admin") {

      navigate(`/${org.orgSlug}/admin-dashboard`);

    } else if (org.role === "employee") {

      navigate(`/${org.orgSlug}/employee-dashboard`);

    } else {

      alert("Invalid role");

    }
  };

  // =====================================================
  // 🔥 EMPTY STATE
  // =====================================================

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="text-slate-500 font-semibold">
          Loading organizations...
        </div>
      </div>
    );
  }

  // =====================================================
  // 🔥 NO ORGS
  // =====================================================

  if (orgs.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-6">

        <div className="bg-white p-10 rounded-3xl shadow-xl w-full max-w-md text-center">

          <Building2
            className="mx-auto mb-5 text-indigo-500"
            size={48}
          />

          <h2 className="text-2xl font-black text-slate-800 mb-3">
            No Organizations Found
          </h2>

          <p className="text-slate-500 mb-6">
            Your account is not linked with any organization.
          </p>

          <button
            onClick={() => navigate("/")}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl font-bold"
          >
            Back to Home
          </button>

        </div>
      </div>
    );
  }

  // =====================================================
  // 🎨 UI
  // =====================================================

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 p-6">

      <motion.div
        initial={{ opacity: 0, y: 25 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white p-10 rounded-3xl shadow-2xl w-full max-w-lg"
      >

        {/* HEADER */}

        <div className="text-center mb-8">

          <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">

            <Building2
              className="text-indigo-600"
              size={30}
            />

          </div>

          <h2 className="text-3xl font-black text-slate-800">
            Select Organization
          </h2>

          <p className="text-slate-500 mt-2">
            Choose your workspace to continue
          </p>

        </div>

        {/* ORGS */}

        <div className="space-y-4">

          {orgs.map((org, index) => (

            <motion.button
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              key={index}
              onClick={() => selectOrg(org)}
              className="w-full p-5 rounded-2xl border border-slate-200 hover:border-indigo-500 hover:bg-indigo-50 transition-all text-left"
            >

              <div className="flex items-center justify-between">

                <div>

                  <h3 className="font-black text-slate-800 text-lg">

                    {org.orgName ||
                      org.name ||
                      "Organization"}

                  </h3>

                  <p className="text-sm text-slate-500 mt-1">
                    {org.orgSlug}
                  </p>

                </div>

                <div>

                  {org.role === "admin" ? (

                    <div className="flex items-center gap-2 bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-bold">

                      <ShieldCheck size={14} />

                      ADMIN

                    </div>

                  ) : (

                    <div className="flex items-center gap-2 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-bold">

                      <Users size={14} />

                      EMPLOYEE

                    </div>

                  )}

                </div>

              </div>

            </motion.button>

          ))}

        </div>

      </motion.div>
    </div>
  );
}